import type { Ref } from 'react'
import {
  CustomSelect,
  type RenderSearchItemProps,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { observer } from 'mobx-react-lite'
import { type Member } from '@/features/iam/members/Member'
import { Avatar } from '@/components/Avatar'
import { type ProInputEditorViewModel } from './ProInputEditorViewModel'
import {
  ProInputEditor,
  ProSearchItemDetails,
} from '@/features/jobs/TransferJobDialog/ProInputEditor'

export interface ProInputEditorContainerProps {
  vm: ProInputEditorViewModel
}

export const ProInputEditorContainer = observer(
  function ProInputEditorContainer({ vm }: ProInputEditorContainerProps) {
    const selectProProps = useCustomSelectStateFromItemSource({
      itemSource: vm.proItemSource,
    })

    return (
      <ProInputEditor
        currentPro={
          vm.jobs.length === 1
            ? {
                avatar: vm.currentChampionPro?.avatar,
                displayName: vm.currentChampionPro?.displayName,
                online: !!vm.currentChampionPro?.user.online,
              }
            : undefined
        }
        proFilter={{
          isQualifiedOnly: vm.canDisplayQualifiedPros,
          isAnyAvailable: vm.canDisplayAllPros,
          onSelectQualifiedOnly: vm.setDisplayQualifiedPros,
          onSelectAnyAvailable: vm.setDisplayAllPros,
        }}
        proError={vm.validation.getError('pro')}
        proSelect={
          <CustomSelect
            {...selectProProps}
            multi={false}
            icon="dropdown"
            iconColor="var(--grey600)"
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={vm.proPlaceholder}
            value={vm.pro}
            onChange={vm.setPro}
            renderSearchItem={(props) => (
              <TransferToProSearchItemContainer {...props} />
            )}
            testId="transfer-to-pro-dropdown"
          />
        }
      />
    )
  },
)

interface TransferToProSearchItemContainerProps extends RenderSearchItemProps<Member | null> {
  ref?: Ref<HTMLDivElement>
}

const TransferToProSearchItemContainer = observer(
  function TransferToProSearchItemContainer({
    item,
    itemToString: _itemToString,
    ref,
    ...itemProps
  }: TransferToProSearchItemContainerProps) {
    return (
      <SearchResultItem
        ref={ref}
        {...itemProps}
        visual={<Avatar size={24} subject={item} />}
        testId={'transfer-to-pro-option-' + item?.user.publicId?.toLowerCase()}
      >
        <ProSearchItemDetails
          name={item?.displayName}
          email={item?.user.email}
        />
      </SearchResultItem>
    )
  },
)
